import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MoonLoader } from "react-spinners";
import "../../assets/css/aunjai-loanregister.css";
import "../../assets/css/aunjai-otp.css";
import ImageInput from "../../components/form/ImageInput/ImageInput";
import SelectInput from "../../components/form/SelectInput";
import TextInput from "../../components/form/TextInput";
import FooterButton from "../../components/ui/Button/FooterButton";
import DatePicker from "../../components/ui/DatePicker/DatePicker";
import { DOCUMENT_NAMES } from "../../constants/documents";
import {
  FORM_INITIAL_VALUES,
  useFormProfileOptions,
} from "../../constants/form-profile";
import { useSyncFormProfile } from "../../features/loans/hooks/useSyncFormProfile";
import { useFileUpload } from "../../hooks/useFileUpload";
import { useLoanStore } from "../../stores/useLoanStore";
import {
  getAdditionalContacts,
  getFormProfileSchema,
} from "../../utils/form-profile";
import Header from "../Header/stepHeader";
import provinceJson from "../Json/allprovince.json";
import OtpInput from "./Otp";

const STATIC_CUSTOMER_TYPE = 1;

const FormProfile = () => {
  const {
    MARITAL_STATUS_OPTIONS,
    EDUCATION_OPTIONS,
    CHILD_COUNT_OPTIONS,
    RELATIONSHIP_OPTIONS_FIRST_PERSON,
    RELATIONSHIP_OPTIONS_SECOND_PERSON,
  } = useFormProfileOptions();
  const [userData, setUserData] = useState(null);
  const { t } = useTranslation("loanProcessForm");
  const [formInitialValues, setFormInitialValues] =
    useState(FORM_INITIAL_VALUES);

  const [loading, setLoading] = useState(false);

  const [shouldShowOtp, setShouldShowOTP] = useState(false);

  const [useCurrentAddress, setUseCurrentAddress] = useState(false);

  const isForeigner = userData?.is_foreigner ?? false;

  const isEn = localStorage.getItem("i18nextLng")?.startsWith("en");

  const provinceData = provinceJson;
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [subDistrictOptions, setSubDistrictOptions] = useState([]);

  const {
    fileInfo: idCardFileInfo,
    propagateFileChange: propagateIdCardFileChange,
    errorMessage: idCardErrorMessage,
    setErrorMessage: setIdCardErrorMessage,
  } = useFileUpload(DOCUMENT_NAMES.ID_CARD);

  const {
    fileInfo: houseLicenseFileInfo,
    propagateFileChange: propagateHouseLicenseFileChange,
    errorMessage: houseLicenseErrorMessage,
    setErrorMessage: setHouseLicenseErrorMessage,
  } = useFileUpload(DOCUMENT_NAMES.HOUSE_LICENSE);

  const {
    fileInfo: passportFileInfo,
    propagateFileChange: propagatePassportFileChange,
    errorMessage: passportErrorMessage,
    setErrorMessage: setPassportErrorMessage,
  } = useFileUpload(DOCUMENT_NAMES.PASSPORT);

  const {
    fileInfo: visaFileInfo,
    propagateFileChange: propagateVisaFileChange,
    errorMessage: visaErrorMessage,
    setErrorMessage: setVisaErrorMessage,
  } = useFileUpload(DOCUMENT_NAMES.VISA);

  const {
    fileInfo: workPermitFileInfo,
    propagateFileChange: propagateWorkPermitFileChange,
    errorMessage: workPermitErrorMessage,
    setErrorMessage: setWorkPermitErrorMessage,
  } = useFileUpload(DOCUMENT_NAMES.WORK_PERMIT);

  const setFormProfileData = useLoanStore((state) => state.setFormProfileData);

  const navigate = useNavigate();

  useSyncFormProfile({
    loadingStateSetter: setLoading,
    userDataSetter: setUserData,
    formInitialValuesSetter: setFormInitialValues,
  });

  const handleBack = () => {
    navigate("/select-loan");
  };

  const handleProvinceChange = (event, setFieldValue) => {
    const selectedProvinceId = event.target.value;
    setSelectedProvince(selectedProvinceId);

    const province = provinceData.find(
      (p) => p.id === parseInt(selectedProvinceId)
    );
    if (province) {
      setFieldValue("idCardProvince", selectedProvinceId);
      setDistrictOptions(province.amphure || []);
      setSubDistrictOptions([]);
      setFieldValue("idCardDistrict", "");
      setFieldValue("idCardSubDistrict", "");
    } else {
      setDistrictOptions([]);
      setSubDistrictOptions([]);
      setFieldValue("idCardDistrict", "");
      setFieldValue("idCardSubDistrict", "");
    }
  };

  const handleDistrictChange = (event, setFieldValue) => {
    const selectedDistrictId = event.target.value;
    const selectedDistrict = selectedProvince
      ? provinceData
          .find((p) => p.id === parseInt(selectedProvince))
          ?.amphure.find((d) => d.id === parseInt(selectedDistrictId))
      : null;

    if (selectedDistrict) {
      setSubDistrictOptions(selectedDistrict.tambon || []);
      setFieldValue("idCardDistrict", selectedDistrictId);
      setFieldValue("idCardSubDistrict", "");
    }
  };

  const handleSubDistrictChange = (event, setFieldValue) => {
    const selectedSubDistrictId = event.target.value;
    const selectedSubDistrict = subDistrictOptions.find(
      (subdistrict) => subdistrict.id === parseInt(selectedSubDistrictId)
    );

    if (selectedSubDistrict) {
      setFieldValue("idCardSubDistrict", selectedSubDistrictId);
      setFieldValue("idCardPostalCode", selectedSubDistrict.zip_code);
    }
  };

  const handleSubmitForm = (formValues) => {
    setIdCardErrorMessage("");

    setHouseLicenseErrorMessage("");

    if (isForeigner) {
      if (!passportFileInfo) {
        setPassportErrorMessage(t("upload1"));
        return;
      }
      if (!visaFileInfo) {
        setVisaErrorMessage(t("upload2"));
        return;
      }
      if (!workPermitFileInfo) {
        setWorkPermitErrorMessage(t("upload3"));
        return;
      }
    } else {
      if (!idCardFileInfo) {
        setIdCardErrorMessage(t("upload4"));
        return;
      }
    }
    const additional_contacts = getAdditionalContacts(formValues);

    const province = provinceData.find(
      (p) => p.id === parseInt(formValues.idCardProvince)
    );
    const district = province?.amphure?.find(
      (d) => d.id === parseInt(formValues.idCardDistrict)
    );
    const subDistrict = district?.tambon?.find(
      (s) => s.id === parseInt(formValues.idCardSubDistrict)
    );

    const thaiIdCardFile = {
      name: DOCUMENT_NAMES.ID_CARD,
      file: idCardFileInfo?.file,
    };

    const thaiHouseLicenseFile = {
      name: DOCUMENT_NAMES.HOUSE_LICENSE,
      file: houseLicenseFileInfo?.file,
    };

    const foreignPassportFile = {
      name: DOCUMENT_NAMES.PASSPORT,
      file: passportFileInfo?.file,
    };

    const foreignVisaFile = {
      name: DOCUMENT_NAMES.VISA,
      file: visaFileInfo?.file,
    };

    const foreignWorkPermitFile = {
      name: DOCUMENT_NAMES.WORK_PERMIT,
      file: workPermitFileInfo?.file,
    };

    const attachment = isForeigner
      ? [
          thaiIdCardFile,
          thaiHouseLicenseFile,
          foreignPassportFile,
          foreignVisaFile,
          foreignWorkPermitFile,
        ]
      : [thaiIdCardFile, thaiHouseLicenseFile];

    const firstPayload = {
      additional_contacts,
      attachment,
      customer_type: STATIC_CUSTOMER_TYPE,
      borrower_id: userData.id,
    };

    setFormProfileData({
      ...firstPayload,
      formValues: {
        ...formValues,
        idCardProvince: province?.name_th,
        idCardDistrict: district?.name_th,
        idCardSubDistrict: subDistrict?.name_th,
      },
    });

    setShouldShowOTP(true);
  };

  const setFirstFillFormData = useLoanStore(
    (state) => state.setFirstFillFormData
  );

  function handleOTPSuccess() {
    // Skip form-step1 (อาชีพ/รายได้) — set defaults and go directly to form-step2
    setFirstFillFormData({
      job_type: "พนักงานประจำ",
      hire_type: "พนักงานประจำ",
      job_name: "อื่นๆ",
      other_job_name: "-",
      jobLevel: "พนักงาน",
      current_company: "-",
      job_experience_in_year: 1,
      monthly_income: 50000,
      out_time_income: 0,
      other_income: 0,
      income_type: "รายเดือน",
      monthly_outcome: 0,
      debt_amount: 0,
      monthly_debt: 0,
      total_debt: 0,
      attachment: [],
    });
    navigate("/loan-process/form-step2");
  }

  function handleIdCardAddressCheckBoxChange(e, fieldValueSetter, values) {
    setUseCurrentAddress(e.target.checked);
    const currentProvince = provinceData.find((p) =>
      isEn
        ? p.name_en === values.currentProvince
        : p.name_th === values.currentProvince
    );

    const newDistrictOptions = currentProvince?.amphure || [];
    const currentDistrict = newDistrictOptions.find((d) =>
      isEn
        ? d.name_en === values.currentDistrict
        : d.name_th === values.currentDistrict
    );

    const newSubDistrictOptions = currentDistrict?.tambon || [];
    const currentSubDistrict = newSubDistrictOptions.find((s) =>
      isEn
        ? s.name_en === values.currentSubDistrict
        : s.name_th === values.currentSubDistrict
    );

    setDistrictOptions(newDistrictOptions);
    setSubDistrictOptions(newSubDistrictOptions);

    const targetAndNewValuesMap = {
      idCardAddress: values.currentAddress,
      idCardProvince: currentProvince?.id,
      idCardDistrict: currentDistrict?.id,
      idCardSubDistrict: currentSubDistrict?.id,
      idCardPostalCode: values.currentPostalCode,
    };

    if (e.target.checked) {
      Object.entries(targetAndNewValuesMap).forEach(
        ([targetField, newValue]) => {
          fieldValueSetter(targetField, newValue, {
            shouldValidate: true,
          });
        }
      );
    } else {
      Object.keys(targetAndNewValuesMap).forEach((targetField) => {
        const clearedValue = "";

        fieldValueSetter(targetField, clearedValue, {
          shouldValidate: true,
        });
      });
    }
  }

  return (
    <>
      <div id="PageFormProfile">
        <Header
          title={t("perosonalDetail")}
          showIcon={false}
          onBack={() => {
            navigate(-1);
          }}
          step={1}
        />
        {/* <StepHeader /> */}
        <div className="wrapContent">
          <div id="pFormProfile">
            <div className="bContent">
              {loading ? (
                <>
                  <MoonLoader size={20} color="#fff" loading={loading} />
                </>
              ) : !shouldShowOtp ? (
                <>
                  <Formik
                    initialValues={formInitialValues}
                    validationSchema={getFormProfileSchema(isForeigner)}
                    onSubmit={(values) => {
                      handleSubmitForm(values);
                    }}
                  >
                    {({ handleSubmit, errors, setFieldValue, values }) => (
                      <>
                        <Form className="bForm" onSubmit={handleSubmit}>
                          <TextInput
                            label={t("startName")}
                            name="title"
                            placeholder={t("startName")}
                            disabled
                          />
                          {/* <div className="bInput">
                          <label className="tText">เพศ</label>
                          <Field
                            type="text"
                            name="title"
                            value={
                              userData?.gender === "male"
                                ? "ชาย"
                                : userData?.gender === "female"
                                ? "หญิง"
                                : "ไม่ระบุ"
                            }
                            placeholder="ชื่อ"
                            disabled
                          />
                          <ErrorMessage
                            name="gender"
                            component="div"
                            className="error"
                          />
                        </div> */}
                          {/* ชื่อ (First Name) */}
                          {isForeigner ? (
                            <>
                              <div className="bInput">
                                <label className="tText">{t("nameEn")}</label>
                                <Field
                                  type="text"
                                  name="firstNameEn"
                                  placeholder={t("name")}
                                  value={userData?.first_name_en}
                                  disabled
                                />
                                <ErrorMessage
                                  name="firstNameEn"
                                  component="div"
                                  className="error"
                                />
                              </div>
                              {/* ชื่อกลาง (Middle Name) */}
                              <div className="bInput">
                                <label className="tText">
                                  {t("middleNameEn")}
                                </label>
                                <Field
                                  type="text"
                                  name="middleNameEn"
                                  placeholder=""
                                  value={
                                    userData?.middle_name_en
                                      ? userData.middle_name_en
                                      : ""
                                  }
                                  disabled
                                />
                              </div>
                              {/* นามสกุล (Last Name) */}
                              <div className="bInput">
                                <label className="tText">
                                  {t("surnameEn")}
                                </label>
                                <Field
                                  type="text"
                                  name="lastNameEn"
                                  placeholder={t("surname")}
                                  value={userData?.last_name_en}
                                  disabled
                                />
                                <ErrorMessage
                                  name="lastNameEn"
                                  component="div"
                                  className="error"
                                />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="bInput">
                                <label className="tText">{t("nameTh")}</label>
                                <Field
                                  type="text"
                                  name="firstNameTh"
                                  value={userData?.first_name_th}
                                  placeholder={t("name")}
                                  disabled
                                />
                                <ErrorMessage
                                  name="firstNameTh"
                                  component="div"
                                  className="error"
                                />
                              </div>
                              {/* ชื่อกลาง (ไทย) — hidden */}
                              {/* นามสกุล (Last Name) */}
                              <div className="bInput">
                                <label className="tText">
                                  {t("surnameTh")}
                                </label>
                                <Field
                                  type="text"
                                  name="lastNameTh"
                                  placeholder={t("surname")}
                                  value={userData?.last_name_th}
                                  disabled
                                />
                                <ErrorMessage
                                  name="lastNameTh"
                                  component="div"
                                  className="error"
                                />
                              </div>
                            </>
                          )}
                          {/* วัน/เดือน/ปีเกิด + หมายเลขบัตร — hidden, ใช้ค่าจาก userData */}
                          {/* เบอร์โทรศัพท์ */}
                          <div className="bInput">
                            <label className="tText">{t("tel")}</label>
                            <Field
                              type="text"
                              name="phoneNumber"
                              value={userData?.phone}
                              placeholder={t("name")}
                              disabled
                            />
                            <ErrorMessage
                              name="phoneNumber"
                              component="div"
                              className="error"
                            />
                          </div>
                          {/* อีเมล์ */}
                          <div className="bInput">
                            <label className="tText">{t("email")}</label>
                            <Field
                              type="text"
                              name="title"
                              value={userData?.email}
                              placeholder={t("name")}
                              disabled
                            />
                            <ErrorMessage
                              name="email"
                              component="div"
                              className="error"
                            />
                          </div>
                          {/* สถานะ, วุฒิการศึกษา, จำนวนบุตร — hidden */}
                          {/* ที่อยู่ปัจจุบัน (read-only จาก userData) */}
                          <div className="bInput">
                            <label className="tText">{t("placeNow")}</label>
                            <Field
                              type="text"
                              name="currentAddress"
                              value={`${userData?.address || ""} ${userData?.sub_district || ""} ${userData?.district || ""} ${userData?.province || ""} ${userData?.zip || ""}`}
                              disabled
                            />
                          </div>
                          {/* ที่อยู่ตามบัตร ปชช — hidden */}
                          {/* บุคคลอ้างอิง 1 + 2 — hidden */}
                          {isForeigner ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "32px",
                              }}
                            >
                              <ImageInput
                                label={t("pass")}
                                description={t("passDesc")}
                                placeholder={t("passDesc2")}
                                {...propagatePassportFileChange()}
                                fileInfo={passportFileInfo}
                                errorMessage={passportErrorMessage}
                              />
                              <ImageInput
                                label={t("uploadVisa")}
                                description={t("uploadVisaDesc")}
                                placeholder={t("uploadVisaDesc2")}
                                {...propagateVisaFileChange()}
                                fileInfo={visaFileInfo}
                                errorMessage={visaErrorMessage}
                              />
                              <ImageInput
                                label={t("uploadId")}
                                description={t("uploadId2")}
                                placeholder={t("uploadId3")}
                                {...propagateWorkPermitFileChange()}
                                fileInfo={workPermitFileInfo}
                                errorMessage={workPermitErrorMessage}
                              />
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "32px",
                              }}
                            >
                              <ImageInput
                                label={t("uploadIdImg")}
                                description={t("uploadIdImg2")}
                                exampleImageUrl="/assets/img/ci/img/img-idcard.png"
                                {...propagateIdCardFileChange()}
                                fileInfo={idCardFileInfo}
                                errorMessage={idCardErrorMessage}
                              />

                              {/* ทะเบียนบ้าน — hidden */}
                            </div>
                          )}
                          <FooterButton
                            onBack={handleBack}
                            submitButtonTitle={t("next")}
                            backButtonTitle={t("back")}
                          />
                        </Form>
                      </>
                    )}
                  </Formik>
                </>
              ) : (
                <OtpInput
                  pageData="formProfile"
                  onOtpSuccess={handleOTPSuccess}
                  phoneNumber={userData?.phone}
                  allowOtpByPass={true} // WARNING : Remove this after testing
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormProfile;
